import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { Route, Switch, useLocation } from "wouter";
import { useEffect, lazy, Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import Home from "./pages/Home";
import CookieBanner from "./components/CookieBanner";

// Code-split heavy pages — loaded only when user navigates to them
const Splyw = lazy(() => import("./pages/Splyw"));
const Cennik = lazy(() => import("./pages/Cennik"));
const ZPsem = lazy(() => import("./pages/ZPsem"));
const Grupy = lazy(() => import("./pages/Grupy"));
const Ognisko = lazy(() => import("./pages/Ognisko"));
const Galeria = lazy(() => import("./pages/Galeria"));
const Kontakt = lazy(() => import("./pages/Kontakt"));
const ONas = lazy(() => import("./pages/ONas"));
const Rezerwacja = lazy(() => import("./pages/Rezerwacja"));
const Polityka = lazy(() => import("./pages/Polityka"));

// Location pages
const KajakiSpala = lazy(() => import("./pages/KajakiSpala"));
const KajakiInowlodz = lazy(() => import("./pages/KajakiInowlodz"));
const KajakiTomaszow = lazy(() => import("./pages/KajakiTomaszow"));
const KajakiSmardzewice = lazy(() => import("./pages/KajakiSmardzewice"));
const KajakiZakosciele = lazy(() => import("./pages/KajakiZakosciele"));
const KajakiGapinin = lazy(() => import("./pages/KajakiGapinin"));
const KajakiDomaniewice = lazy(() => import("./pages/KajakiDomaniewice"));
const KajakiNoweMiasto = lazy(() => import("./pages/KajakiNoweMiasto"));
const KajakiMysiakowiec = lazy(() => import("./pages/KajakiMysiakowiec"));

// Event pages
const KrolewskiSplyw2026 = lazy(() => import("./pages/KrolewskiSplyw2026"));
const SystemPanel = lazy(() => import("./pages/SystemPanel"));

function ScrollToTop() {
  const [location] = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location]);
  return null;
}

function Router() {
  return (
    <>
      <ScrollToTop />
      <main id="main-content">
        <Suspense fallback={<div className="min-h-screen" />}>
          <Switch>
            <Route path="/" component={Home} />
            <Route path="/splyw" component={Splyw} />
            <Route path="/cennik" component={Cennik} />
            <Route path="/z-psem" component={ZPsem} />
            <Route path="/grupy-i-eventy" component={Grupy} />
            <Route path="/ognisko" component={Ognisko} />
            <Route path="/galeria" component={Galeria} />
            <Route path="/kontakt" component={Kontakt} />
            <Route path="/o-nas" component={ONas} />
            <Route path="/rezerwacja" component={Rezerwacja} />
            <Route path="/polityka-prywatnosci" component={Polityka} />
            {/* Location pages */}
            <Route path="/kajaki-spala" component={KajakiSpala} />
            <Route path="/kajaki-inowlodz" component={KajakiInowlodz} />
            <Route path="/kajaki-tomaszow-mazowiecki" component={KajakiTomaszow} />
            <Route path="/kajaki-smardzewice" component={KajakiSmardzewice} />
            <Route path="/kajaki-zakosciele" component={KajakiZakosciele} />
            <Route path="/kajaki-gapinin" component={KajakiGapinin} />
            <Route path="/kajaki-domaniewice" component={KajakiDomaniewice} />
            <Route path="/kajaki-nowe-miasto" component={KajakiNoweMiasto} />
            <Route path="/kajaki-mysiakowiec" component={KajakiMysiakowiec} />
            {/* Event pages */}
            <Route path="/krolewski-splyw-2026" component={KrolewskiSplyw2026} />
            <Route path="/system" component={SystemPanel} />
            <Route path="/404" component={NotFound} />
            <Route component={NotFound} />
          </Switch>
        </Suspense>
      </main>
    </>
  );
}

function App() {
  const [location] = useLocation();
  const isSystemPanel = location.startsWith("/system");

  return (
    <HelmetProvider>
      <ErrorBoundary>
        <ThemeProvider defaultTheme="light">
          <TooltipProvider>
            <Toaster />
            <Router />
            {!isSystemPanel && <CookieBanner />}
          </TooltipProvider>
        </ThemeProvider>
      </ErrorBoundary>
    </HelmetProvider>
  );
}

export default App;
